import {
  ActionIcon,
  AppShell,
  Avatar,
  Badge,
  Box,
  Burger,
  Group,
  Menu,
  NavLink,
  ScrollArea,
  Text,
  Tooltip,
  useMantineColorScheme,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { Spotlight, spotlight, type SpotlightActionData } from "@mantine/spotlight";
import { IconHelp, IconLogout, IconMoon, IconSearch, IconSun } from "@tabler/icons-react";
import type { ComponentType, ReactNode } from "react";
import { Link, NavLink as RouterNavLink, Outlet, useNavigate } from "react-router";
import { semanticColor } from "@how/design-system";
import { CountBadge } from "./CountBadge";

/** The sidebar's width; the header's brand is centred over it. */
const NAVBAR_WIDTH = 240;

export interface AppFrameNavItem {
  readonly to: string;
  readonly label: string;
  readonly icon: ComponentType<{ size?: number | string; stroke?: number | string }>;
  /** Match only the exact path (for "/"). */
  readonly end?: boolean;
  /** Something waiting there (the inbox's unread conversations): a badge beside the label, hidden at 0. */
  readonly count?: number;
  /** What the count counts, for the link's accessible name ("Communications, 3 unread"). */
  readonly countLabel?: string;
}

/**
 * Whether a navigation item matches the current path.
 *
 * Mantine's `NavLink` only sets `data-active` when it is given the `active` prop; wrapping it in a router NavLink is
 * not enough, because that only adds `aria-current`. Without this the active row fell back to Mantine's own tint and
 * none of the project's four navigation states applied. Exported so the matching rules are tested rather than
 * assumed.
 */
export function isNavItemActive(pathname: string, item: AppFrameNavItem): boolean {
  const exact = item.end ?? item.to === "/";
  if (exact) return pathname === item.to;
  return pathname === item.to || pathname.startsWith(`${item.to}/`);
}

/** `to` is an in-app route; `href` a plain link (full page load); `onSelect` an action such as a hand-off. */
export type AppFrameAccountLink = {
  readonly label: string;
  /** Where it goes, shown quieter under the words (an address, R10); part of the item's accessible name. */
  readonly description?: string;
  readonly icon: ComponentType<{ size?: number | string; stroke?: number | string }>;
} & ({ readonly to: string } | { readonly href: string } | { readonly onSelect: () => void });

/**
 * An account item's words and, under them, the quieter line naming where it goes. Spans, not divs: a menu item is a
 * button, which holds phrasing content only.
 */
export function AccountItemText({
  label,
  description,
}: {
  readonly label: string;
  readonly description?: string | undefined;
}) {
  if (description === undefined) return <>{label}</>;
  return (
    <>
      <Text span display="block" size="sm">
        {label}
      </Text>
      <Text span display="block" size="xs" c="dimmed">
        {description}
      </Text>
    </>
  );
}

/** The item's accessible name: its words, then where it goes when it names it (R10); otherwise the words alone. */
function accountLinkName(link: AppFrameAccountLink): string | undefined {
  return link.description === undefined ? undefined : `${link.label}, ${link.description}`;
}

export interface AppFrameAccount {
  readonly email: string;
  /** The person's name, for the initials on the account button; the email's are used without one. */
  readonly name?: string;
  /** Short descriptor under the email in the account menu (role, church). */
  readonly label?: string;
  /** Account pages (Security, Profile) listed above "Sign out". */
  readonly links?: readonly AppFrameAccountLink[];
  /**
   * HoW's help center (https://<platform>/help, ADR 0059): a "Help center" item in the account menu and in Spotlight,
   * opening in a new tab so the screen the person was on stays where it was.
   */
  readonly helpUrl?: string;
  readonly onSignOut: () => void;
}

export interface AppFrameProps {
  /** Left side of the header: product name, surface badge, tenant name. */
  readonly brand: ReactNode;
  /** Navigation is convenience, never authorization: filter items before passing them. */
  readonly nav: readonly AppFrameNavItem[];
  readonly account: AppFrameAccount;
  /** Extra Spotlight actions beyond "Go to <nav item>". */
  readonly actions?: readonly SpotlightActionData[];
  /** Rendered right after the brand: a tenant switcher, for example. */
  readonly switcher?: ReactNode;
  /** Rendered in the main area; defaults to the router Outlet. */
  readonly children?: ReactNode;
}

/**
 * The layer-2 shell from the UI standards: solid 56px header and hairline sidebar on the panel surface, mobile drawer
 * behind a burger, color-scheme toggle, account menu with sign-out, and Spotlight (mod+K) wired to the navigation.
 * Never frosted glass: blur is allowed only on modal and drawer scrims.
 */
export function AppFrame({ brand, nav, account, actions = [], switcher, children }: AppFrameProps) {
  const [opened, { toggle, close }] = useDisclosure(false);
  const { colorScheme, toggleColorScheme } = useMantineColorScheme();
  const navigate = useNavigate();
  const spotlightActions: SpotlightActionData[] = [
    ...nav.map((item) => ({
      id: item.to,
      label: item.label,
      description: `Go to ${item.label}`,
      onClick: () => {
        void navigate(item.to);
      },
    })),
    ...actions,
    ...(account.helpUrl === undefined
      ? []
      : [
          {
            id: "how-help-center",
            label: "Help center",
            description: "Step-by-step answers, in a new tab",
            onClick: () => {
              window.open(account.helpUrl, "_blank", "noopener");
            },
          },
        ]),
  ];

  return (
    <AppShell
      header={{ height: 56 }}
      navbar={{ width: NAVBAR_WIDTH, breakpoint: "sm", collapsed: { mobile: !opened } }}
      padding="md"
    >
      <AppShell.Header className="how-appbar">
        <Group h="100%" px="md" justify="space-between" wrap="nowrap">
          <Group gap="sm" wrap="nowrap" style={{ minWidth: 0 }}>
            <Burger
              opened={opened}
              onClick={toggle}
              hiddenFrom="sm"
              size="sm"
              aria-label="Toggle navigation"
            />
            {/* Centred over the sidebar from the sm breakpoint up (owner, 2026-10-01, #325); see .how-appbar-brand. */}
            <Box
              className="how-appbar-brand"
              style={{ "--how-navbar-width": `${String(NAVBAR_WIDTH)}px` }}
            >
              {brand}
            </Box>
            {switcher}
          </Group>
          <Group gap="xs" wrap="nowrap">
            <Tooltip label="Search (Ctrl+K)">
              <ActionIcon
                variant="subtle"
                color="gray"
                aria-label="Open search"
                onClick={spotlight.open}
              >
                <IconSearch size={18} />
              </ActionIcon>
            </Tooltip>
            <Tooltip
              label={colorScheme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
            >
              <ActionIcon
                variant="subtle"
                color="gray"
                aria-label="Toggle color scheme"
                onClick={toggleColorScheme}
              >
                {colorScheme === "dark" ? <IconSun size={18} /> : <IconMoon size={18} />}
              </ActionIcon>
            </Tooltip>
            <Menu position="bottom-end" width={240}>
              <Menu.Target>
                <ActionIcon variant="subtle" color="gray" radius="xl" aria-label="Account menu">
                  {/*
                   * Not `color="initials"`: Mantine picks a colour from the name out of a palette that knows nothing
                   * about our surfaces, and it landed on a green that measured 2.75:1 on a white header (2026-09-23).
                   * This is the signed-in person's own avatar, so the variety bought nothing anyway. The brand fill
                   * is the one pair the theme tests hold to AA in both schemes.
                   */}
                  <Avatar
                    size="sm"
                    radius="xl"
                    name={account.name?.trim() || account.email}
                    color="brand"
                    variant="filled"
                  />
                </ActionIcon>
              </Menu.Target>
              <Menu.Dropdown>
                <Menu.Label>
                  <Text size="xs" truncate>
                    {account.email}
                  </Text>
                </Menu.Label>
                {/*
                 * A plain line, not a disabled item: Mantine fades a disabled item to 0.6, which left this badge at
                 * 2.52:1 light and 2.83:1 dark (ruling D14). It names the account; there is nothing to choose.
                 */}
                {account.label === undefined ? null : (
                  <Box px="sm" pb={6}>
                    <Badge variant="light" size="sm" color={semanticColor("neutral")}>
                      {account.label}
                    </Badge>
                  </Box>
                )}
                <Menu.Divider />
                {(account.links ?? []).map((link) => {
                  const shared = {
                    leftSection: <link.icon size={16} />,
                    "aria-label": accountLinkName(link),
                    children: <AccountItemText label={link.label} description={link.description} />,
                  };
                  return "onSelect" in link ? (
                    <Menu.Item key={link.label} onClick={link.onSelect} {...shared} />
                  ) : "href" in link ? (
                    <Menu.Item key={link.href} component="a" href={link.href} {...shared} />
                  ) : (
                    <Menu.Item key={link.to} component={Link} to={link.to} {...shared} />
                  );
                })}
                {account.helpUrl === undefined ? null : (
                  <Menu.Item
                    component="a"
                    href={account.helpUrl}
                    target="_blank"
                    rel="noopener"
                    aria-label="Help center (opens in a new tab)"
                    leftSection={<IconHelp size={16} />}
                  >
                    Help center
                  </Menu.Item>
                )}
                <Menu.Item leftSection={<IconLogout size={16} />} onClick={account.onSignOut}>
                  Sign out
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
          </Group>
        </Group>
      </AppShell.Header>
      <AppShell.Navbar p="sm" className="how-sidebar">
        <ScrollArea>
          {nav.map((item) => (
            <NavLink
              key={item.to}
              component={RouterNavLink}
              to={item.to}
              end={item.end ?? item.to === "/"}
              active={isNavItemActive(location.pathname, item)}
              label={item.label}
              leftSection={<item.icon size={18} stroke={1.6} />}
              {...((item.count ?? 0) > 0
                ? {
                    // Visible words first (SC 2.5.3): "Communications, 3 unread".
                    "aria-label": `${item.label}, ${String(item.count)}${item.countLabel === undefined ? "" : ` ${item.countLabel}`}`,
                    rightSection: <CountBadge count={item.count ?? 0} />,
                  }
                : {})}
              onClick={close}
            />
          ))}
        </ScrollArea>
      </AppShell.Navbar>
      <AppShell.Main>{children ?? <Outlet />}</AppShell.Main>
      <Spotlight
        actions={spotlightActions}
        shortcut={["mod + K"]}
        nothingFound="Nothing found"
        searchProps={{ placeholder: "Search" }}
      />
    </AppShell>
  );
}
